import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

function CircleIcon({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: 'var(--red-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: '1rem',
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

export default function Footer() {
  const { t, prefix } = useLanguage();

  const quickLinks = [
    { labelKey: 'footer.home', path: '/' },
    { labelKey: 'footer.events', path: '/events' },
    { labelKey: 'footer.schedule', path: '/schedule' },
    { labelKey: 'footer.news', path: '/news' },
    { labelKey: 'footer.schools', path: '/schools' },
    { labelKey: 'footer.aboutUs', path: '/about' },
    { labelKey: 'footer.contact', path: '/contact' },
  ];

  return (
    <footer>
      {/* Main dark footer */}
      <div
        style={{
          background: 'var(--dark)',
          color: 'var(--gray-400)',
          padding: '64px 0 48px',
        }}
      >
        <div
          className="container"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 48,
          }}
        >
          {/* Get in Touch */}
          <div>
            <h3
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'var(--white)',
                marginBottom: 24,
              }}
            >
              {t('footer.getInTouch')}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <CircleIcon>✉</CircleIcon>
                <div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{t('footer.email')}</div>
                  <a
                    href="mailto:info@caaoc.org"
                    style={{ color: 'var(--white)', fontSize: '0.95rem' }}
                  >
                    info@caaoc.org
                  </a>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <CircleIcon>☎</CircleIcon>
                <div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{t('footer.phone')}</div>
                  <a
                    href="tel:+17146603180"
                    style={{ color: 'var(--white)', fontSize: '0.95rem' }}
                  >
                    (714) 660-3180
                  </a>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <CircleIcon>📍</CircleIcon>
                <div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{t('footer.location')}</div>
                  <span style={{ color: 'var(--white)', fontSize: '0.95rem' }}>
                    Irvine, Orange County, CA
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'var(--white)',
                marginBottom: 24,
              }}
            >
              {t('footer.quickLinks')}
            </h3>
            <div
              style={{
                background: 'var(--dark-secondary)',
                borderRadius: 'var(--radius-md)',
                padding: 24,
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                }}
              >
                {quickLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={prefix + link.path}
                    style={{
                      color: 'var(--gray-400)',
                      fontSize: '0.95rem',
                      padding: '6px 0',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-400)')}
                  >
                    {t(link.labelKey)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div
        style={{
          background: '#12121f',
          padding: '20px 0',
          borderTop: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <div
          className="container"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: 'var(--red-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.9rem',
              }}
            >
              C
            </div>
            <span style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
              &copy; {new Date().getFullYear()} {t('footer.copyright')}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <Link
              to="/privacy"
              style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-500)')}
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to="/terms"
              style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-500)')}
            >
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          footer .container {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </footer>
  );
}
